import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  ProfileForm,
  Captcha,
  useTranslations,
  useErrorContext, useRegionContext,
} from "tds_shared_ui";
import {
  CaptchaService,
  TermsService,
  UserService,
} from "../services";
import { ConsentForm } from "../components/UI/ConsentForm";

import "./RegistrationPage.css";
import {useNavigate} from "react-router-dom";

export function RegistrationPage() {
  // Refs
  const captchaRef = useRef();

  // State
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [termsData, setTermsData] = useState(null);
  const [formValues, setFormValues] = useState({
    userId: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    phone: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    locksmithID: "",
    locksmithPasscode: "",
    acdelcoAcctNum: "",
    companyEmail: "",
    vat: "",
    companyName: "",
    preferredLanguage: "",
  });

  const [consentData, setConsentData] = useState(null);
  const [showConsent, setShowConsent] = useState(false);
  const [hideConsentChecks, setHideConsentChecks] = useState(false);
  const [readOnly, setReadOnly] = useState(false);

  // Use the new translations and error context hooks
  const { countries, countryStateMap, locale } = useRegionContext();
  const { translate } = useTranslations(["user", "common"]);
  const { clearError, displayError, displayAlert } = useErrorContext();
  const navigate = useNavigate();

  useEffect(() => {
    if (countries !== null && countries.length === 1) {
      let country = countries[0].value;
      if (countryStateMap !== null) {
        const statesArr = countryStateMap[country];
        setStates(statesArr);
        setFormValues((prev) => ({ ...prev, country: country }));
      }
    }
    if (locale) {
      setFormValues((prev) => ({
        ...prev,
        preferredLanguage: locale.split("_")[0],
      }));
    }
    handleStaticContent();
  }, [countries, countryStateMap, locale]);

  useEffect(() => {
    if (!countries) return;
    if (formValues.country === "" && countries.length === 1) {
      let country = countries[0].value;
      if (countryStateMap !== null) {
        const statesArr = countryStateMap[country];
        setStates(statesArr);
        setFormValues((prev) => ({ ...prev, country: country }));
      }
    }
  }, [countries, countryStateMap, formValues.country]);

  const handleChange = useCallback((name, value, callback) => {
    setFormValues((prev) => {
      let newFormValues = { ...prev, [name]: value };
      if (name === "country" && prev.country !== value) {
        newFormValues.state = "";
        if (value != null) {
          setStates(countryStateMap[value]);
        } else {
          setStates([]);
        }
      }
      return newFormValues;
    });
    if (callback) callback();
  }, [countryStateMap]);

  function handleCancel() {
    navigate(-1);
  }

  function getUserObject(formValues) {
    let {
      addressLine1,
      addressLine2,
      city,
      state,
      zipCode,
      country,
      confirmPassword,
      locksmithID,
      locksmithPasscode,
      ...user
    } = formValues;
    user.locksmithID = locksmithID;
    user.passcode = locksmithPasscode;
    user.address = {
      addressLine1,
      addressLine2,
      city,
      state,
      zipCode,
      country,
    }

    return user;
  }

  const onValidationError = useCallback(() => {
    displayError(translate("ERROR_FORM_VALIDATION"));
  }, [displayError, translate]);

  const handleSubmit = useCallback(() => {
    setLoading(true);
    setShowConsent(false);
    clearError();

    let captchaData = CaptchaService.getCaptchaData(captchaRef);
    let user = getUserObject(formValues);
    user = Object.assign(user, captchaData);

    UserService.createUser(user, {
      headers: { "Accept-Language": locale.replace("_", "-") },
    })
      .then((user) => {
        setLoading(false);
        setReadOnly(true);
        displayAlert(translate("MESSAGE_SUCCESS_REGISTRATION"), "success");
        const address = user.address || {};
        setFormValues({
          userId: user.userId || "",
          password: "",
          confirmPassword: "",
          firstName: user.firstName || "",
          middleName: user.middleName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          phone: user.phone || "",
          addressLine1: address.addressLine1 || "",
          addressLine2: address.addressLine2 || "",
          city: address.city || "",
          state: address.state || "",
          zipCode: address.zipCode || "",
          country: address.country || "",
          locksmithID: user.locksmithID || "",
          locksmithPasscode: "",
          acdelcoAcctNum: user.acdelcoAcctNum || "",
          companyEmail: user.companyEmail || "",
          vat: user.vat || "",
          companyName: user.companyName || "",
          preferredLanguage: user.preferredLanguage || "",
        });
      })
      .catch((e) => {
        displayError(e);
        setLoading(false);
        setReadOnly(false);
        setHideConsentChecks(true);
      });
  }, [formValues, locale, translate, displayAlert, displayError, clearError]);

  const showConsentForm = useCallback(() => {
    let captchaData = CaptchaService.getCaptchaData(captchaRef);
    if (!captchaData.cText) {
      displayAlert(translate("ERROR_CAPTCHA_MUST_BE_COMPLETED"), "error");
      return;
    }
    if (formValues.country === "KR") {
      setShowConsent(true);
      setHideConsentChecks(false);
    } else {
      handleSubmit();
    }
  }, [formValues.country, displayAlert, translate, handleSubmit]);

  const hideTerms = useCallback(() => {
    setShowConsent(false);
  }, []);

  function handleStaticContent() {
    const tdstermsconditions = "tdstermsconditions";
    TermsService.getTerms(tdstermsconditions, locale)
      .then((data) => {
        setTermsData(data.content);
      })
      .catch(() => {});
    TermsService.getTerms("krconsent", locale)
      .then((data) => {
        setConsentData(data.content);
      })
      .catch(() => {});
  }

  let renderCaptcha = null;

  if (!readOnly) {
    renderCaptcha = () => (
      <div className="pb-4">
        <Captcha captchaRef={captchaRef} />
      </div>
    );
  }
  return (
    <div className="registrationPage">
      {countries && (
        <ProfileForm
          showTerms={true}
          showLogin={true}
          submit={showConsentForm}
          loading={loading}
          onValidationError={onValidationError}
          readOnly={readOnly}
          formValues={formValues}
          states={states}
          termsHtmlString={termsData}
          renderCaptcha={renderCaptcha}
          onChange={handleChange}
          onCancel={handleCancel}
        />
      )}
      <ConsentForm
        onSuccess={handleSubmit}
        open={showConsent}
        onCancel={hideTerms}
        text={consentData}
        hideChecks={hideConsentChecks}
      />
    </div>
  );
}